import apiService from './api';

class AITrainingService {
  constructor() {
    this.cache = {
      customer: null,
      provider: null,
      lastFetch: {
        customer: 0,
        provider: 0
      }
    };
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutes
  }

  // Get customer training data
  async getCustomerTrainingData() {
    const now = Date.now();
    
    // Check if we have cached data that's still fresh
    if (this.cache.customer && (now - this.cache.lastFetch.customer) < this.cacheTimeout) {
      return this.cache.customer;
    }

    try {
      const response = await apiService.get('/customer/ai-training');
      if (response.data && response.data.success) {
        this.cache.customer = response.data.data;
        this.cache.lastFetch.customer = now;
        return response.data.data;
      }
    } catch (error) {
      console.error('Error fetching customer training data:', error);
    }

    // Return default fallback data if API fails
    return this.getDefaultCustomerTrainingData();
  }

  // Get provider training data
  async getProviderTrainingData() {
    const now = Date.now();
    
    // Check if we have cached data that's still fresh
    if (this.cache.provider && (now - this.cache.lastFetch.provider) < this.cacheTimeout) {
      return this.cache.provider;
    }

    try {
      const response = await apiService.get('/provider/ai-training');
      if (response.data && response.data.success) {
        this.cache.provider = response.data.data;
        this.cache.lastFetch.provider = now;
        return response.data.data;
      }
    } catch (error) {
      console.error('Error fetching provider training data:', error);
    }

    // Return default fallback data if API fails
    return this.getDefaultProviderTrainingData();
  }

  // Default customer training data (fallback)
  getDefaultCustomerTrainingData() {
    return {
      greetings: [
        "Hello! I'm your customer service assistant. How can I help you today?",
        "Hi there! Welcome to NiStylez beauty and wellness platform. What can I assist you with?",
        "Greetings! I'm here to help you find the perfect beauty service. What are you looking for?"
      ],
      services: [
        "We offer a wide range of beauty and wellness services including Hair Styling, Facial, Manicure, Pedicure, Massage, Makeup, Waxing, Spa treatments, and more. What type of service are you interested in?",
        "Our platform connects you with qualified beauty and wellness service providers. You can browse by category or search for specific services like hair styling, skincare, nail care, and massage.",
        "From hair styling to spa treatments, we have beauty and wellness service providers for all your needs. What category interests you?"
      ],
      booking: [
        "To book a beauty or wellness service, browse our qualified service providers, select a service like hair styling or facial, choose your preferred time slot, and complete the booking process.",
        "Booking is easy! Find a beauty service provider, pick a service (hair, nails, massage, etc.), select your date and time, and confirm your booking.",
        "You can book beauty and wellness services through our platform by selecting a provider, choosing your service, and picking an available time slot."
      ],
      payment: [
        "We accept various payment methods including credit cards, debit cards, and digital wallets. All payments are processed securely.",
        "Payment is handled securely through our platform. You can pay using your preferred payment method when booking.",
        "We offer multiple secure payment options. You'll be charged when you confirm your booking."
      ],
      cancellation: [
        "You can cancel your booking up to 24 hours before the scheduled time. Cancellations are subject to our cancellation policy.",
        "To cancel a booking, go to your bookings section and select the booking you want to cancel. Cancellation policies vary by provider.",
        "Cancellations are allowed with advance notice. Check your booking details for specific cancellation terms."
      ],
      support: [
        "If you need help with your beauty service booking or have any questions, you can contact our customer support team through this chat or email us at support@nistylez.com",
        "For immediate assistance with your beauty and wellness services, use this chat or reach out to our support team. We're here to help!",
        "Customer support is available 24/7 for all your beauty service needs. You can also check our FAQ section for common questions about bookings, payments, and services."
      ],
      profile: [
        "You can update your profile information, manage your bookings, and view your service history in the Profile section.",
        "Your profile contains your personal information, booking history, and account settings. You can edit these anytime.",
        "Access your profile to view past bookings, update personal details, and manage your account preferences."
      ],
      ratings: [
        "After each service, you can rate and review your service provider. Your feedback helps other customers make informed decisions.",
        "We encourage you to rate your service providers after each booking. This helps maintain quality and assists other customers.",
        "You can leave ratings and reviews for service providers after your booking is completed. Your feedback is valuable to our community."
      ]
    };
  }

  // Default provider training data (fallback)
  getDefaultProviderTrainingData() {
    return {
      greetings: [
        "Hello! I'm your business assistant. How can I help you grow your beauty and wellness business today?",
        "Hi there! Welcome to NiStylez provider platform. I'm here to help you manage your services and grow your business.",
        "Greetings! I'm your business partner for success. What can I help you with today?"
      ],
      onboarding: [
        "To get started as a provider, complete your profile, add your services, set your availability, and verify your credentials. I can guide you through each step.",
        "New to our platform? Let me help you set up your business profile, add services, and start accepting bookings from customers.",
        "Ready to start your beauty business journey? Complete your profile setup, add your services, and begin accepting bookings."
      ],
      services: [
        "You can add and manage your beauty services like hair styling, facials, nail care, massage, and more. Each service can have its own pricing and description.",
        "Manage your service offerings by adding new services, updating prices, setting descriptions, and organizing them by categories.",
        "Your services are the heart of your business. Add detailed descriptions, set competitive prices, and showcase your expertise."
      ],
      bookings: [
        "View and manage your upcoming bookings, confirm appointments, and communicate with customers about their service requirements.",
        "Your booking dashboard shows all appointments, customer details, and service requirements. You can confirm, reschedule, or manage bookings here.",
        "Stay organized with your booking calendar. View upcoming appointments, customer preferences, and manage your schedule efficiently."
      ],
      payments: [
        "Track your earnings, view payment history, and manage your payout settings. Payments are processed securely and transferred to your account.",
        "Your earnings dashboard shows completed services, pending payments, and payout history. Set up your preferred payout method.",
        "Monitor your income with detailed payment tracking, view transaction history, and manage your payout preferences."
      ],
      customers: [
        "View your customer profiles, service history, preferences, and feedback. Build lasting relationships with your regular clients.",
        "Your customer database helps you track service history, preferences, and build strong client relationships for repeat business.",
        "Understand your customers better with detailed profiles, service history, and feedback to provide personalized experiences."
      ],
      support: [
        "Need help growing your business or technical support? Contact our provider support team through this chat or email providers@nistylez.com",
        "For business growth tips, technical support, or platform assistance, reach out to our dedicated provider support team.",
        "We're here to help you succeed! Get business advice, technical support, and platform assistance whenever you need it."
      ],
      profile: [
        "Manage your business profile, update your bio, add professional photos, and showcase your expertise to attract more customers.",
        "Your profile is your business card. Keep it updated with professional photos, detailed bio, and certifications to build trust.",
        "Optimize your profile to attract customers. Add professional photos, detailed descriptions, and highlight your expertise and certifications."
      ],
      earnings: [
        "Track your monthly earnings, view detailed analytics, and understand your revenue trends to grow your business strategically.",
        "Your earnings analytics show performance metrics, popular services, and revenue trends to help you make informed business decisions.",
        "Monitor your business performance with detailed earnings reports, customer analytics, and growth insights."
      ],
      reviews: [
        "Manage customer reviews and ratings. Respond to feedback professionally to build trust and attract more customers.",
        "Customer reviews are crucial for your business. Respond thoughtfully to feedback and use insights to improve your services.",
        "Build your reputation through excellent service and professional responses to customer reviews and ratings."
      ]
    };
  }

  // Clear cache (useful for forcing refresh)
  clearCache(appType = null) {
    if (appType) {
      this.cache[appType] = null;
      this.cache.lastFetch[appType] = 0;
    } else {
      this.cache.customer = null;
      this.cache.provider = null;
      this.cache.lastFetch.customer = 0;
      this.cache.lastFetch.provider = 0;
    }
  }
}

export default new AITrainingService();
